import CryptoAES from 'crypto-js/aes';
import CryptoENC from 'crypto-js/enc-utf8';
import { MutableRefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { Loading } from '../components';
import { ReportOpenMetricsView, UserOrgRole } from '../generated/hooks';
import {
  ANONYMOUS_RESPONDENT_ID,
  getAlertsData,
  getPrintableReportDate,
  getRootTeams,
  getTopicGroupTranslation,
} from '../lib/reportHelper';
import { IReport } from '../lib/reports/base';
import AnonymousReport from './components/AnonymousReport';
import { Conversation } from './components/Conversation';
import { ReportOpenMetricsSender } from './components/ObserverEventSender';
import ReportContainer from './components/ReportContainer';
import { ReportMetrics } from './components/ReportMetrics';
import { SCROLL_TO_RESPONDENT_TABLE_PARAM } from '../lib/reports/reportUtils';

interface IProps {
  report: IReport;
  publicAccess?: boolean;
  accessToken: string;
  setNavbarContent?: (content: React.ReactNode) => void;
  reload?: () => void;
}

const Alerts = ({ report, publicAccess, accessToken, setNavbarContent, reload }: IProps) => {
  const { t } = useTranslation('Report');
  const { token, respondentId, seqDate } = useParams<{ token: string; respondentId: string; seqDate: string }>();
  const location = useLocation();

  const getSearchParamsForProcessReport = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('page');

    const positionIds = searchParams.get('position_ids');
    const recruiterIds = searchParams.get('recruiter_ids');
    const managerIds = searchParams.get('manager_ids');

    if (positionIds) {
      searchParams.set('position', positionIds);
    }
    if (recruiterIds) {
      searchParams.set('recruiter', recruiterIds);
    }
    if (managerIds) {
      searchParams.set('manager', managerIds);
    }

    return searchParams.toString() ? `?${searchParams.toString()}` : '';
  };
  const scrollRef = useRef() as MutableRefObject<HTMLDivElement>;
  const reportContainerRef = useRef() as MutableRefObject<HTMLDivElement>;
  const selectedRespondentId = publicAccess
    ? CryptoAES.decrypt(decodeURIComponent(respondentId), report.id.toString()).toString(CryptoENC)
    : respondentId;
  let alerts = getAlertsData(report, t, true).filter((alert) => alert.messages.length);
  if (seqDate?.length > 0) {
    alerts = alerts.filter((alert) => alert.seqDate?.includes(seqDate));
  }

  const respondentDetail = alerts.find((alert) => alert.respondentId === selectedRespondentId);

  const accessReportSequencePath = publicAccess ? 'process-results' : 'report-sequence';
  const accessReportPath = publicAccess ? 'results' : 'report';
  const channelId = report.responses?.find((r) => r.respondentId === selectedRespondentId)?.id;

  useEffect(() => {
    if (setNavbarContent) {
      setNavbarContent(null);
    }
  }, [setNavbarContent]);

  if (!report.responses) {
    return <Loading />;
  }
  return (
    <ReportContainer
      ref={reportContainerRef}
      date={getPrintableReportDate(report.metadata?.surveyStart, report.metadata?.surveyEnd, t)}
      rootTeams={getRootTeams(report)!}
      title={respondentDetail ? respondentDetail.name : t('unknownRespondent')}
      about={respondentId === ANONYMOUS_RESPONDENT_ID ? t('anonymousAnswersDescription') : null}
      breadcrumb={
        report.surveyGroup
          ? [
              {
                title: getTopicGroupTranslation(report.surveyGroup.topicGroup, report.metadata?.language).value,
                link: `/${accessReportSequencePath}/${token}${getSearchParamsForProcessReport()}`,
              },
              {
                title: report.reportDefinition?.titleLocal || '',
                link: `/${accessReportPath}/${token}${(() => {
                  const p = new URLSearchParams(window.location.search);
                  p.set(SCROLL_TO_RESPONDENT_TABLE_PARAM, 'true');
                  return p.toString() ? `?${p.toString()}` : '';
                })()}`,
              },
              { title: respondentDetail ? respondentDetail.name : t('unknownRespondent') },
            ]
          : !publicAccess ||
              report.metadata?.accepted ||
              !report.metadata?.respondent ||
              report.metadata?.respondent?.organizationRole === UserOrgRole.OrgAdmin
            ? [
                {
                  title: t('backToReport'),
                  link: `/${accessReportPath}/${token}${(() => {
                    const p = new URLSearchParams(window.location.search);
                    p.set(SCROLL_TO_RESPONDENT_TABLE_PARAM, 'true');
                    return p.toString() ? `?${p.toString()}` : '';
                  })()}`,
                  onClick: () => {
                    window.history.back();
                  },
                },
              ]
            : []
      }
      organizationName={report.metadata?.organization.name || ''}
      respondentName={
        report.metadata?.respondent
          ? `${report.metadata.respondent.firstname} ${report.metadata.respondent.surname}`
          : undefined
      }
    >
      {selectedRespondentId === ANONYMOUS_RESPONDENT_ID ? (
        <AnonymousReport report={report} accessToken={token} topicGroupId={undefined} publicAccess={publicAccess} />
      ) : (
        <section className={'section'}>
          {publicAccess && <ReportMetrics reportAccessKey={token} trackTime />}
          <div className={'container container--md'}>
            {respondentDetail && (
              <div
                ref={respondentId === selectedRespondentId ? scrollRef : undefined}
                id={respondentId}
                key={respondentId}
                className={'my-8'}
              >
                <Conversation
                  report={report}
                  messages={respondentDetail.messages}
                  team={respondentDetail.team}
                  name={report.reportDefinition?.titleLocal!}
                  endsAt={respondentDetail.date}
                  reload={reload}
                />
                <ReportOpenMetricsSender
                  orgAdmin={!publicAccess}
                  reportAccessKey={accessToken}
                  channelIds={channelId ? [channelId] : []}
                  reportView={ReportOpenMetricsView.RespondentInPhase}
                />
              </div>
            )}
          </div>
        </section>
      )}
    </ReportContainer>
  );
};

export default Alerts;
